import API_BASE from '../config';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import LordIcon from '../components/LordIcon';
import LORDICONS from '../lordicons';

const Join: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [status, setStatus] = useState<'loading' | 'joining' | 'success' | 'error'>('loading');
  const [info, setInfo] = useState<{ businessName: string; role: string } | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!code) { setStatus('error'); setErrorMsg('No invite code.'); return; }

    // 1. Fetch invite info
    fetch(`${API_BASE}/api/invite/info/${code}`)
      .then(r => r.json())
      .then(async data => {
        if (data.error) { setStatus('error'); setErrorMsg(data.error); return; }
        setInfo({ businessName: data.businessName, role: data.role });

        // 2. Check if user is logged in
        const meRes = await fetch(`${API_BASE}/api/users/me`, { credentials: 'include' });
        if (!meRes.ok) {
          // Not logged in - check if they have an account (we can't tell, so offer both)
          setStatus('error');
          setErrorMsg('__not_logged_in__');
          return;
        }

        // 3. Logged in - auto join
        setStatus('joining');
        const joinRes = await fetch(`${API_BASE}/api/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });
        const joinData = await joinRes.json();
        if (!joinRes.ok) { setStatus('error'); setErrorMsg(joinData.error || 'Join failed'); return; }
        setStatus('success');
        setTimeout(() => navigate('/dashboard'), 2000);
      })
      .catch(() => { setStatus('error'); setErrorMsg('Network error'); });
  }, [code]);

  const toLogin = () => navigate(`/login?invite=${code}`);
  const toRegister = () => navigate(`/register?invite=${code}`);

  // ── Not logged in ──
  if (status === 'error' && errorMsg === '__not_logged_in__') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
            <LordIcon src={LORDICONS.users} size={32} color="#0065F4" trigger="loop" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">{t('join.invited')}</h1>
          {info && (
            <p className="text-sm text-gray-500 mb-6">
              {t('join.invited.to')} <strong>{info.businessName}</strong>
            </p>
          )}
          <div className="flex flex-col gap-3">
            <button onClick={toLogin} className="w-full px-5 py-3 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors flex items-center justify-center gap-2">
              <LordIcon src={LORDICONS.user} size={18} color="#FFFFFF" trigger="hover" target="button" />
              {t('join.login.and.join')}
            </button>
            <button onClick={toRegister} className="w-full px-5 py-3 border border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
              <LordIcon src={LORDICONS.plus} size={18} color="#6B7280" trigger="hover" target="button" />
              {t('join.create.account')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <LordIcon src={LORDICONS.warning} size={32} color="#dc2626" trigger="in" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('join.invalid.code')}</h1>
          <p className="text-sm text-gray-500 mb-6">{errorMsg}</p>
          <button onClick={() => navigate('/login')} className="px-5 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-colors">
            {t('join.go.to.login')}
          </button>
        </div>
      </div>
    );
  }

  // ── Success ──
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md text-center">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <LordIcon src={LORDICONS.check} size={32} color="#16a34a" trigger="in" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">{t('join.success')}</h1>
          {info && <p className="text-sm text-gray-500">{t('join.success.sub')} <strong>{info.businessName}</strong></p>}
          <p className="text-xs text-gray-400 mt-4">{t('join.redirecting')}</p>
        </div>
      </div>
    );
  }

  // ── Loading / joining ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md text-center">
        <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4 animate-pulse">
          <LordIcon src={LORDICONS.clock} size={32} color="#0065F4" trigger="loop" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">{status === 'joining' ? t('join.joining') : t('join.loading')}</h1>
      </div>
    </div>
  );
};

export default Join;
